import { useEffect, useRef, useState } from "react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { Button, EmptyState, Icon, Skeleton, Tabs, Textarea } from "@cntrl/ui";
import { ApiError, errorMessage } from "../../../app/hooks/apiError.js";
import { pushToast } from "../../../app/toasts.js";
import { mediaKindForFile, useMediaDownload, useUploadMedia } from "../../../app/hooks/useMedia.js";
import {
  useAdvanceDepartments, useUpdateAdvanceDepartment,
  type AdvanceDepartmentView, type LookId,
} from "../../../app/hooks/useProduction.js";
import styles from "./AdvanceCatalogue.module.scss";

/**
 * The TWELVE department tabs for one advance (ART-146, spec §6.3): Audio · Backline · Lighting · Video ·
 * Carps · SFX · Automation · Rigging · Trucking · Bussing · VIP/Merch · Catering/Hospitality.
 *
 * **Each tab is files + notes, and that is the whole scaffold** — the advance-docs pivot (2026-07-28,
 * *"files and notes only"*) retired the structured Audio Bible, and its CSV import died with it. Do not
 * rebuild either.
 *
 * **The tab STRIP comes from the server**, all twelve, in tab order, empty ones included. A client that
 * derived the list from what came back would show a band with one populated department exactly one tab.
 *
 * **A save writes ONE department.** The route is narrow for a reason the client has to respect in kind: the
 * pack PUT beside it is a whole-document replace, and an editor showing one tab that went through it would
 * reconcile away the other eleven on every save.
 */
export function AdvanceDepartments(props: { lookId: LookId; canWrite: boolean }) {
  const q = useAdvanceDepartments(props.lookId);
  const [active, setActive] = useState<string>("audio");

  if (!q.data) {
    return q.isError
      ? <EmptyState title="Couldn't load the departments" description={q.error instanceof ApiError ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>} />
      : <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
          <Skeleton variant="line" width="70%" /><Skeleton variant="line" width="45%" />
        </div>;
  }

  const items = q.data.items ?? [];
  const current = items.find((d) => d.key === active) ?? items[0];
  if (!current) return null;

  return (
    <div className={styles.depts}>
      <Tabs
        label="Advance departments"
        active={current.key ?? "audio"}
        onChange={setActive}
        className={styles.deptTabs}
        tabs={items.map((d) => ({
          key: d.key ?? "",
          // The COUNT rides the label rather than a separate badge: it is the only thing that distinguishes
          // twelve otherwise-identical tabs at a glance, and a tab with nothing in it stays plain.
          label: (d.files?.length ?? 0) > 0 ? `${d.label} ${d.files!.length}` : (d.label ?? ""),
        }))}
      />
      <DepartmentPanel
        // Keyed on the department AND the advance: the panel holds a note draft, and a switch must reseed it
        // rather than carry one department's typing into another's textarea.
        key={`${props.lookId ?? "regular"}:${current.key}`}
        lookId={props.lookId}
        department={current}
        canWrite={props.canWrite}
      />
    </div>
  );
}

function DepartmentPanel(props: { lookId: LookId; department: AdvanceDepartmentView; canWrite: boolean }) {
  const { department: d } = props;
  const save = useUpdateAdvanceDepartment(props.lookId);
  const { uploadMedia } = useUploadMedia();
  const { download } = useMediaDownload();
  const fileInput = useRef<HTMLInputElement>(null);

  const files = d.files ?? [];
  const [notes, setNotes] = useState(d.notes ?? "");
  const [uploading, setUploading] = useState(false);

  // The panel is keyed on the department so this only fires when the SERVER's copy changes under a panel that
  // stayed mounted — a save's own response, which the draft should follow.
  useEffect(() => { setNotes(d.notes ?? ""); }, [d.notes]);

  const notesDirty = notes !== (d.notes ?? "");

  /** Every write sends the department's CURRENT file set plus whatever changed — the route reconciles within
   *  this department, so an omitted file is a deleted file and the list has to be complete. */
  const write = (
    nextFiles: { fileId: string | null; mediaId: string; label: string | null; visibleToVenue: boolean }[],
    nextNotes: string,
    success: string,
  ) =>
    save.mutate(
      { department: d.key ?? "", notes: nextNotes.trim() || null, files: nextFiles },
      { onSuccess: () => pushToast(success, "success"), onError: (e) => pushToast(errorMessage(e, "Couldn't save."), "error") },
    );

  const currentFiles = () => files.map((f) => ({
    fileId: f.fileId ?? null, mediaId: f.mediaId!, label: f.label ?? null, visibleToVenue: f.visibleToVenue ?? true,
  }));

  const onPick = async (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of Array.from(picked)) {
        const asset = await uploadMedia(file, { kind: mediaKindForFile(file), purpose: "production" });
        added.push({ fileId: null, mediaId: asset.mediaId!, label: file.name, visibleToVenue: true });
      }
      // Saved IMMEDIATELY rather than into a draft: an upload has already crossed the network, and leaving
      // it as an unsaved row means a band that closes the tab has paid for bytes nothing points at.
      write([...currentFiles(), ...added], notes, added.length === 1 ? "File added." : `${added.length} files added.`);
    } catch (e) {
      pushToast(errorMessage(e, "Couldn't upload the file."), "error");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onRemove = (fileId: string) =>
    write(currentFiles().filter((f) => f.fileId !== fileId), notes, "File removed.");

  return (
    <div className={styles.deptPanel}>
      <div className={styles.deptFiles}>
        {files.length === 0 && <p className={styles.hint}>Nothing for {d.label} yet.</p>}
        {files.map((f) => (
          <div key={f.fileId} className={styles.fileChip}>
            <Icon icon={FileText} size={16} className={styles.fileChipIcon} />
            <div className={styles.fileChipMain}>
              <p className={styles.fileChipName} title={f.label ?? f.originalFilename ?? "File"}>
                {f.label ?? f.originalFilename ?? "File"}
              </p>
              <p className={styles.fileChipMeta}>{fileMeta(f.originalFilename, f.sizeBytes)}</p>
            </div>
            {f.mediaId && (
              <button type="button" className={styles.chipBtn} aria-label={`Download ${f.label ?? "file"}`}
                onClick={() => void download(f.mediaId!, f.originalFilename ?? f.label)
                  .catch((e) => pushToast(errorMessage(e, "Couldn't download the file."), "error"))}>
                <Icon icon={Download} size={16} />
              </button>
            )}
            {props.canWrite && f.fileId && (
              <button type="button" className={styles.chipBtn} aria-label={`Remove ${f.label ?? "file"}`}
                onClick={() => onRemove(f.fileId!)} disabled={save.isPending}>
                <Icon icon={Trash2} size={16} />
              </button>
            )}
          </div>
        ))}

        {props.canWrite && (
          <>
            <input ref={fileInput} type="file" multiple className={styles.hiddenInput}
              onChange={(e) => void onPick(e.target.files)} />
            <Button variant="ghost" size="sm" disabled={uploading || save.isPending}
              onClick={() => fileInput.current?.click()}>
              <Icon icon={Upload} size={16} /> {uploading ? "Uploading…" : `Add a file to ${d.label}`}
            </Button>
          </>
        )}
      </div>

      <Textarea
        label={`${d.label} notes`}
        rows={6}
        value={notes}
        readOnly={!props.canWrite}
        placeholder={`${d.label} notes — specs, contacts, anything the advance needs.`}
        onChange={(e) => setNotes(e.target.value)}
      />
      {props.canWrite && (
        <div className={styles.deptSave}>
          <Button size="sm" disabled={!notesDirty || save.isPending}
            onClick={() => write(currentFiles(), notes, `${d.label} notes saved.`)}>
            {save.isPending ? "Saving…" : "Save notes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function fileMeta(filename: string | null | undefined, size: number | null | undefined): string {
  const parts: string[] = [];
  if (filename) parts.push(filename);
  if (size && size > 0) {
    parts.push(size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`);
  }
  return parts.join(" · ");
}
